import { createClient } from '@supabase/supabase-js';

const url = process.env.REACT_APP_SUPABASE_URL;
const publishableKey = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

if (!url || !publishableKey) {
  // Fail loudly if env is misconfigured
  // eslint-disable-next-line no-console
  console.error(
    'Supabase env mancanti. Verifica REACT_APP_SUPABASE_URL e REACT_APP_SUPABASE_PUBLISHABLE_KEY.'
  );
}

export const supabase = createClient(url, publishableKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    storageKey: 'restaurantos-auth',
  },
});

const CEO_TENANT_CONTEXT_STORAGE_KEY = 'restaurantos-ceo-tenant-context';
const CEO_TENANT_CONTEXT_TTL_MS = 8 * 60 * 60 * 1000;

function browserStorage() {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

function readCeoTenantContext(userId) {
  const storage = browserStorage();
  if (!storage || !userId) return null;
  try {
    const saved = JSON.parse(storage.getItem(CEO_TENANT_CONTEXT_STORAGE_KEY) || 'null');
    if (saved?.userId !== userId || (saved.tenantId !== null && typeof saved.tenantId !== 'string')) return null;
    if (!Number.isFinite(saved.expiresAt) || saved.expiresAt <= Date.now()) {
      storage.removeItem(CEO_TENANT_CONTEXT_STORAGE_KEY);
      return null;
    }
    return saved.tenantId || 'none';
  } catch {
    storage.removeItem(CEO_TENANT_CONTEXT_STORAGE_KEY);
    return null;
  }
}

function saveCeoTenantContext(session, tenantId) {
  const storage = browserStorage();
  const userId = session?.user?.id;
  if (!storage || !userId || typeof tenantId !== 'string' || !tenantId) return;
  try {
    storage.setItem(CEO_TENANT_CONTEXT_STORAGE_KEY, JSON.stringify({
      userId,
      tenantId,
      expiresAt: Date.now() + CEO_TENANT_CONTEXT_TTL_MS,
    }));
  } catch {
    // The signed server cookie remains the fallback when browser storage is unavailable.
  }
}

function clearCeoTenantContext(session) {
  const storage = browserStorage();
  const userId = session?.user?.id;
  if (storage && userId) {
    try {
      storage.setItem(CEO_TENANT_CONTEXT_STORAGE_KEY, JSON.stringify({
        userId,
        tenantId: null,
        expiresAt: Date.now() + CEO_TENANT_CONTEXT_TTL_MS,
      }));
      return;
    } catch {
      // Fall through to removing the local selector.
    }
  }
  try {
    storage?.removeItem(CEO_TENANT_CONTEXT_STORAGE_KEY);
  } catch {
    // The signed server cookie remains the fallback when browser storage is unavailable.
  }
}

function authenticatedHeaders(session, initialHeaders) {
  const headers = new Headers(initialHeaders || {});
  if (session?.access_token) {
    headers.set('Authorization', `Bearer ${session.access_token}`);
  }
  const tenantId = readCeoTenantContext(session?.user?.id);
  if (tenantId) headers.set('X-CEO-Tenant-Context', tenantId);
  return headers;
}

// Wrapper that always attaches the current access token to a fetch to our backend.
export async function apiFetch(path, options = {}) {
  const { data: { session } } = await supabase.auth.getSession();
  const headers = authenticatedHeaders(session, options.headers);
  const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (!headers.has('Content-Type') && options.body && !isFormData) {
    headers.set('Content-Type', 'application/json');
  }
  const base = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(`${base}${path}`, {
    ...options,
    headers,
    credentials: options.credentials || 'include',
  });
  const contentType = res.headers.get('content-type') || '';
  const body = contentType.includes('application/json') ? await res.json() : await res.text();
  if (!res.ok) {
    const message = typeof body === 'object' && body?.detail ? body.detail : (typeof body === 'string' ? body : `HTTP ${res.status}`);
    const err = new Error(message);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  if (path === '/api/ceo/tenant-context') {
    const method = String(options.method || 'GET').toUpperCase();
    if (method === 'POST') saveCeoTenantContext(session, body?.active_tenant_id);
    if (method === 'DELETE') clearCeoTenantContext(session);
  }
  return body;
}

// Authenticated blob fetch for downloads and Web Share.
export async function apiFetchBlob(path) {
  const { data: { session } } = await supabase.auth.getSession();
  const base = process.env.REACT_APP_BACKEND_URL;
  const res = await fetch(`${base}${path}`, {
    headers: authenticatedHeaders(session),
    credentials: 'include',
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try { const j = await res.json(); msg = j.detail || msg; } catch (_) { /* ignore */ }
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return await res.blob();
}

// Authenticated blob download. Opens the file inline in a new tab (PDF).
export async function apiDownloadBlob(path, filename) {
  const blob = await apiFetchBlob(path);
  const url = URL.createObjectURL(blob);
  // Open in new tab for inline PDF preview + user save
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
